/**
 * Ensures a date value is parsed as UTC.
 * Backend dates are stored as UTC but may be serialized without a 'Z' suffix
 * (e.g. "2025-03-23T06:47:00" instead of "2025-03-23T06:47:00Z") because
 * EF Core reads DateTime values from SQL Server with Kind = Unspecified.
 * Without the 'Z', JavaScript's Date constructor treats the string as local
 * time, shifting the value by the user's UTC offset.
 *
 * @param {string|Date|null|undefined} value - Date string, Date object, or nullish
 * @returns {Date|null} A correctly-constructed Date object, or null if invalid/falsy
 */
function parseAsUtc(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  // Append 'Z' only to ISO-like strings that lack any timezone indicator
  if (typeof value === 'string' && !value.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(value)) {
    value = value + 'Z';
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Formats a date value to "d MMMM yyyy" format (e.g. "3 March 2025").
 * @param {string|Date|null|undefined} value - Date string, Date object, or nullish
 * @returns {string} Formatted date or 'Never' if value is falsy
 */
export function formatDate(value) {
  const d = parseAsUtc(value);
  if (!d) return value ? 'Invalid date' : 'Never';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Formats a date value to "d MMMM yyyy, HH:mm" format (e.g. "3 March 2025, 14:30").
 * @param {string|Date|null|undefined} value - Date string, Date object, or nullish
 * @returns {string} Formatted datetime or 'Never' if value is falsy
 */
export function formatDateTime(value) {
  const d = parseAsUtc(value);
  if (!d) return value ? 'Invalid date' : 'Never';
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${date}, ${time}`;
}

/**
 * Parses a backend date string as UTC and returns the epoch milliseconds.
 * Useful for computing accurate time differences against Date.now().
 *
 * @param {string|Date|null|undefined} value - Date string, Date object, or nullish
 * @returns {number|null} Epoch milliseconds or null if invalid/falsy
 */
export function parseUtcMs(value) {
  const d = parseAsUtc(value);
  return d ? d.getTime() : null;
}
